import {
  Component,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { Error } from 'src/app/models/error.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { KeycloakService } from '../../services/keycloak/keycloak.service';
import { Log } from 'src/app/models/services/logs/add-log.model';
import { LogService } from '../../services/logs/log.service';
import { MaintenanceListFields } from 'src/app/models/maintenance-list-fields.models';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from '../../services/users/users.service';
import { entAnimations } from 'src/app/animations';
import { environment } from 'src/environments/environment';
import { Subscription, lastValueFrom } from 'rxjs';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { UserInfo } from 'src/app/models/user-info.model';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild('mobileMtncContainer') mobileContainer: ElementRef;
  isBusy = false;
  canGetUser = true; // can get user
  user: UserInfo;
  error: Error = { error: { message: '', code: '' } };
  translationService: TranslateService;
  maintenanceMessage: MaintenanceListFields[] = [];
  panelOpenState = false;
  isStartDisabled = false;
  isPasswordResetDisabled = false;
  private readonly subscription: Subscription = new Subscription();

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  constructor(
    _translateService: TranslateService,
    private _keycloakService: KeycloakService,
    private _router: Router,
    private _userService: UsersService,
    private _logService: LogService,
    private _graphService: GraphService,
    private _localStorageService: LocalStorageService,
    private _routeStatusService: RouteStatusService
  ) {
    this.translationService = _translateService;

    this.getListofMaintenancesMessages().then(() => {
      //This line can show maintenance message test
      //this.testMaintenancesMessages()
      this.hideMobileMaintenanceMessage();
    });
  }

  hideMobileMaintenanceMessage() {
    if (this.maintenanceMessage.length == 0 && this.mobileContainer) {
      this.mobileContainer.nativeElement.style.display = 'none';
    }
  }

  ngOnInit() {
    if (this._userService.currentUserValue != null) {
      this._router.navigate(['/dashboard']);
    } else {
      this.isLogIn();
    }

    //Maintenance mode settings
    const urls = ['/*', '/start', '/password-change'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          this.isStartDisabled = statuses['/start'];
          this.isPasswordResetDisabled = statuses['/password-change'];
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  startActivation(): void {
    this._router.navigate(['/start']);
  }

  logout(): void {
    this._keycloakService.logout(environment.logoutURL);
    this._keycloakService.clearToken();
  }

  login(): void {
    this._keycloakService.login();
  }

  testMaintenancesMessages(): void {
    const maintenanceTest = new MaintenanceListFields();
    maintenanceTest.title = 'Maintenance test Dev';
    maintenanceTest.titre_fr = 'Maintenance test Dev';
    maintenanceTest.texte_fr =
      "Voici un exemple de test de maintenance afin d'afficher le message de maintenance et de détecter les différents problèmes s'il y en a";
    maintenanceTest.titre_de = 'Wartung test Dev';
    maintenanceTest.texte_de =
      'Hier ist ein Beispiel für einen Wartungstest, um die Wartungsmeldung anzuzeigen und verschiedene Probleme zu erkennen, falls es welche gibt';
    this.maintenanceMessage.push(maintenanceTest);
  }

  async getListofMaintenancesMessages(): Promise<void> {
    try {
      this.maintenanceMessage = await lastValueFrom(
        this._graphService.getListMaintenanceMessage()
      );

      // Filter location = Front page
      this.maintenanceMessage = this.maintenanceMessage.filter(
        x => x.location == 'Accueil'
      );
    } catch {
      /* empty */
    }
  }

  async isLogIn(): Promise<void> {
    if (this._keycloakService.isAuthenticated()) {
      try {
        this.isBusy = true;
        this.user = await lastValueFrom(this._userService.getCurrentUser());

        this.canGetUser = true;

        lastValueFrom(
          this._logService.addLog(
            new Log(
              'SUCCESS',
              this.user.oid,
              this.user.oid,
              'isLogIn()',
              'User has been successfully logged in',
              this._localStorageService.getItem<string>('userDefaultGE'),
              this.user.roles.map(role => role.name)
            )
          )
        );

        if (this.isPasswordResetDisabled) {
          //Workaround to prevent AuthGuard to force user to change password when then component is in maintenance
          const user =
            this._localStorageService.getItem<UserInfo>('currentUser');
          if (user.mustChangePwd) {
            user.mustChangePwd = false;
            this._localStorageService.setItem('currentUser', user);
            this._userService.currentUserSubject.next(user);
          }
        }

        if (this._userService.currentUserValue.mustChangePwd) {
          this._router.navigate(['/password-change']);
        } else {
          this._router.navigate(['/dashboard']);
        }
      } catch (error) {
        if (error.statusText == 'Unknown Error') {
          this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (error.error) {
            this.error = error.error;
          } else if (error.error === undefined) {
            this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
          }
        }
        this.isBusy = false;
        this.canGetUser = false;
      } finally {
        this.isBusy = false;
      }
    }
  }
}
